import { getAPIToken, logoutFrontend } from 'helpers/authentication'
import { client } from './generated/client.gen'

export const baseUrlByEnvironment = () => {
  switch (import.meta.env.VITE_APP_ENV) {
    case 'production':
      return import.meta.env.VITE_APP_API_URL_PROD
    case 'staging':
      return import.meta.env.VITE_APP_API_URL_STAGING
    default:
      return import.meta.env.VITE_APP_API_URL
  }
}

/*
 * Configure the generated SDK client (src/api/generated) once. Every SDK
 * function (getUsers, putUsersByIdBan, ...) uses this shared client. There is
 * no caching layer: every call hits the API. The token is read per request,
 * so login/logout take effect immediately.
 */
client.setConfig({
  // env URLs end with `/api/`; SDK paths start with `/`, the client strips
  // the trailing slash
  baseUrl: baseUrlByEnvironment(),
  headers: { Accept: 'application/json' },
})

client.interceptors.request.use((request) => {
  request.headers.set('X-Api-Key', getAPIToken() ?? '')
  return request
})

client.interceptors.response.use((response) => {
  if (response.status === 401) logoutFrontend()
  return response
})

/**
 * Some operations declare `X-Api-Key` as an explicit header parameter, so
 * their generated `Data` type requires `headers`. The request interceptor
 * above sets the same header anyway; pass this only where TS asks for it.
 */
export const authHeaders = () => ({ 'X-Api-Key': getAPIToken() ?? '' })

/** Error thrown by `unwrap` for non-2xx responses or `success: false` bodies. */
export class ApiError extends Error {
  status?: number
  body?: unknown

  constructor(message: string, status?: number, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

type ErrorBody = {
  errors?: string[] | string
  error?: string
  message?: string
}

const messageFrom = (body: unknown, fallback: string) => {
  if (typeof body === 'string' && body) return body
  const { errors, error, message } = (body ?? {}) as ErrorBody
  if (Array.isArray(errors) && errors.length) return errors.join('\n')
  if (typeof errors === 'string' && errors) return errors
  return error || message || fallback
}

type SdkResult<TData> = {
  data?: TData
  error?: unknown
  response?: Response
}

/**
 * Awaits a generated SDK call and returns its typed `data`, throwing an
 * `ApiError` when the request failed (network error, HTTP >= 400) or when the
 * backend answered `{ success: false }`.
 *
 * @example const users = await unwrap(getUsers({ query: { page } }))
 */
export const unwrap = async <TData>(
  call: Promise<SdkResult<TData>>
): Promise<TData> => {
  let result: SdkResult<TData>
  try {
    result = await call
  } catch (error) {
    throw new ApiError(messageFrom(error, 'Errore di rete'), undefined, error)
  }

  const { data, error, response } = result
  const status = response?.status
  if (error !== undefined || (response && !response.ok)) {
    throw new ApiError(
      messageFrom(error, `Errore ${status ?? ''}`.trim()),
      status,
      error
    )
  }
  if ((data as { success?: boolean } | undefined)?.success === false) {
    throw new ApiError(messageFrom(data, 'Errore'), status, data)
  }
  return data as TData
}

/** Message to show for any error caught around `unwrap`. */
export const errorMessage = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message || fallback : fallback
